import { BellRinging, BellSlash, Bell } from "@phosphor-icons/react";
import { isPushSupported } from "@/lib/push";
import usePushCard from "./push/usePushCard";
import PushPermissionActions from "./push/PushPermissionActions";
import PushTypeToggles from "./push/PushTypeToggles";

/**
 * Card para o admin ativar/desativar Web Push Notifications neste dispositivo
 * e escolher quais alertas globais estão ativos.
 * Toda lógica em `usePushCard`; UI dividida em `PushPermissionActions` e
 * `PushTypeToggles`.
 */
export default function PushNotifyCard() {
  const p = usePushCard();
  if (!isPushSupported()) return <UnsupportedCard />;

  return (
    <div className="glass p-6 space-y-5" data-testid="push-card">
      <Header active={p.active} denied={p.denied} />

      {p.denied && (
        <div className="text-sm border border-red-300 bg-red-50 text-red-800 p-3">
          Você bloqueou as notificações para este site. Abra as configurações do navegador (cadeado ao lado da URL) → Notificações → Permitir, e recarregue a página.
        </div>
      )}

      <PushPermissionActions
        active={p.active}
        denied={p.denied}
        busy={p.busy}
        onEnable={p.onEnable}
        onDisable={p.onDisable}
        onTest={p.onTest}
      />

      <PushTypeToggles cfg={p.cfg} savingCfg={p.savingCfg} saveCfg={p.saveCfg} />
    </div>
  );
}

function Header({ active, denied }) {
  let badgeClass = "bg-zinc-50 border-zinc-400 text-zinc-600";
  let badgeLabel = "inativo";
  if (active) { badgeClass = "bg-emerald-50 border-emerald-700 text-emerald-800"; badgeLabel = "ativo"; }
  else if (denied) { badgeLabel = "bloqueado"; }

  return (
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div>
        <div className="flex items-center gap-2 mb-1">
          {active
            ? <BellRinging size={22} weight="fill" className="text-emerald-600" />
            : <Bell size={22} weight="bold" />}
          <h2 className="font-head text-xl font-black">Notificações Push</h2>
        </div>
        <p className="text-sm text-zinc-600 max-w-xl">
          Receba alertas nativos no celular quando entrar pedido novo ou passar do prazo. A permissão é por dispositivo — ative em cada aparelho que você usar.
        </p>
      </div>
      <div className={`px-2 py-0.5 text-[10px] uppercase tracking-wider border ${badgeClass}`} data-testid="push-status-badge">
        {badgeLabel}
      </div>
    </div>
  );
}

function UnsupportedCard() {
  return (
    <div className="glass p-6 space-y-2" data-testid="push-card">
      <div className="flex items-center gap-2">
        <BellSlash size={20} weight="bold" />
        <h2 className="font-head text-xl font-black">Notificações Push</h2>
      </div>
      <p className="text-sm text-zinc-600">
        Este navegador não suporta Web Push Notifications. Use Chrome/Edge no Android/desktop, ou instale o app (PWA) para receber alertas nativos.
      </p>
    </div>
  );
}
